
import { Component, Prop, Mixins, Vue, Watch, Emit } from 'vue-property-decorator';
import Input from '../../../../core/components/Generics/Input.vue';
import FilterByDateRange from './FilterByDateRange.vue';
import Combobox from '../../../../core/components/Generics/Combobox/Combobox.vue';
import MultipleSelect from '../../../../core/components/Generics/MultipleSelect/MultipleSelect.vue';
import { ClaimTableFilters } from '../../../../core/components/Generics/Table/SwTable/Table';
import { UserInfo } from '../../../../core/auth/services/auth.service';
import { showAppraiserFilter as canSeeAppraiserFilter, showCustomerFilter as canSeeCustomerFilter } from '../../claims-list-visibility';

/** Matches Combobox + vue-multiselect (`label="name"`, `track-by="id"`). */
type EventTypeComboModel = { id: number; name: string } | null;


@Component({
    components: {
        Input,
        Combobox,
        FilterByDateRange,
        MultipleSelect
    }
})
export default class SinisterFilters extends Vue{
    @Prop({default: null}) propertiesData: object;
    @Prop({default(){return []}}) appraisersOptions: object[];
    @Prop({default(){return []}}) customersOptions: object[];
    @Prop({default: false}) isSowing: boolean;


    loadingProperties: boolean = false;
    noOptionsFound: string = 'No hay opciones que coincidan con la búsqueda';

    eventTypeOptions: Array<object> = [];
    appraiserptions: Array<object> = [];
    visibleModalFilterByDateRange: boolean = false;
    filtersDateRangeSelected: any = [];
    filtersEventTypeSelected: EventTypeComboModel = null;
    filtersEventTypeSelectedRv: boolean = false;
    searchQuery: string = '';
    selectedAppraiser: any[] = []
    selectedCustomer: any[] = []

    get userInfo(): UserInfo | null {
        return this.$store.getters['auth/userInfo'] || null;
    }

    // Reglas por rol en `claims-list-visibility.ts`: Aseguradora si hay opciones y no es auditor; Tasadores solo
    // auditores y Smartway.
    get showCustomerFilter(): boolean {
        return !!this.customersOptions && this.customersOptions.length >= 1 && canSeeCustomerFilter(this.userInfo);
    }

    get showAppraiserFilter(): boolean {
        return canSeeAppraiserFilter(this.userInfo);
    }

    get hasActiveDateFilter(): boolean {
        return this.filtersDateRangeSelected && 
               this.filtersDateRangeSelected.length > 0 &&
               this.filtersDateRangeSelected[0] !== '' &&
               this.filtersDateRangeSelected[1] !== '';
    }

    getAppraiserLabel(appraiser: any): string {
        return appraiser.mail || appraiser.name || appraiser.id || '';
    }

    onSelectAppraiser(item) {
        this.selectedAppraiser.push(item);
        this.searchAction();
    }

    onRemoveAppraiser(item) {
        this.selectedAppraiser = this.selectedAppraiser.filter(d => d.id !== item.id);
        this.searchAction();
    }

    getCustomerLabel(customer: any): string {
        return customer.name !== undefined && customer.name !== null ? String(customer.name) : String(customer.id);
    }

    onSelectCustomer(item) {
        this.selectedCustomer.push(item);
        this.searchAction();
    }

    onRemoveCustomer(item) {
        this.selectedCustomer = this.selectedCustomer.filter(d => d.id !== item.id);
        this.searchAction();
    }

    updateSearchQuery(value: string){
        this.searchQuery = value;
        // Trigger search automatically when typing
        // Use $nextTick to ensure Vue has updated the reactive property
        this.$nextTick(() => {
            this.searchAction();
        });
    }

    

    updateFiltersEventTypeSelected(eventType: EventTypeComboModel) {
        this.filtersEventTypeSelected = eventType;
        // Trigger search automatically when event type changes
        this.$nextTick(() => {
            this.searchAction();
        });
    }

    updateResetValueEventTypeSelected(value: boolean){
        this.filtersEventTypeSelectedRv = false;
    }

    updateFiltersDateRange(dateRange){
        this.filtersDateRangeSelected = dateRange;
        // Close the modal after updating
        this.visibilityModalFilterByDateRange(false);
        // Trigger search with new date range
        this.$nextTick(() => {
            this.searchAction();
        });
    }


    mounted(){
        let self = this;
        this.$nextTick(() => {
            self.loadOptions();
        });
    }

    @Watch('propertiesData')
    onPropertiesDataChanged(newVal) {
        if (newVal) {
            this.loadOptions();
        }
    }

    loadOptions(){
        if (!this.propertiesData) {
            return;
        }

        // Load event type options from properties
        if (this.propertiesData['claim_event_types']) {
            this.eventTypeOptions = Object.entries(this.propertiesData['claim_event_types']).map(([id, name]) => ({
                id: parseInt(id),
                name: name
            }));
        }
        this.$nextTick(() => {
            this.syncEventTypeSelectionWithOptionsList();
        });
    }

    /** When options load after restore (or list changes), align model with option shape so multiselect shows the label. */
    private syncEventTypeSelectionWithOptionsList() {
        if (this.filtersEventTypeSelected === null) {
            return;
        }
        if (!this.eventTypeOptions || this.eventTypeOptions.length === 0) {
            return;
        }
        const id = Number(this.filtersEventTypeSelected.id);
        const found = this.eventTypeOptions.find((o: any) => Number(o.id) === id) as
            | { id: number; name: string }
            | undefined;
        if (found != null) {
            this.filtersEventTypeSelected = { id: found.id, name: String(found.name) };
        }
    }

    @Watch('eventTypeOptions', { deep: true })
    onEventTypeOptionsChanged() {
        this.$nextTick(() => {
            this.syncEventTypeSelectionWithOptionsList();
        });
    }

    /** Maps persisted event_type id to the same object shape as Combobox options (`id` + `name`). */
    private buildEventTypeComboSelection(idNum: number): { id: number; name: string } {
        const found = this.eventTypeOptions.find((o: any) => Number(o.id) === idNum) as
            | { id: number; name?: string }
            | undefined;
        if (found != null) {
            return {
                id: found.id,
                name: found.name != null ? String(found.name) : String(found.id),
            };
        }
        return { id: idNum, name: String(idNum) };
    }

    /** Hydrate filter controls from persisted API params without emitting search. */
    restoreUIFromSearchParams(params: ClaimTableFilters) {
        this.loadOptions();

        this.searchQuery = params.query != null && String(params.query).trim() !== ''
            ? String(params.query).trim()
            : '';

        const et = params.event_type;
        if (et !== undefined && et !== null && String(et) !== '') {
            this.filtersEventTypeSelected = this.buildEventTypeComboSelection(Number(et));
            this.$nextTick(() => {
                this.syncEventTypeSelectionWithOptionsList();
            });
        } else {
            this.filtersEventTypeSelected = null;
        }

        const start = params.start_date;
        const end = params.end_date;
        if (start && end && String(start) !== '' && String(end) !== '') {
            this.filtersDateRangeSelected = [String(start), String(end)];
        } else {
            this.filtersDateRangeSelected = [];
        }

        const appr = params.appraisers;
        if (appr && String(appr).trim() !== '' && this.appraisersOptions && this.appraisersOptions.length > 0) {
            const ids = String(appr).split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
            this.selectedAppraiser = ids
                .map(id => this.appraisersOptions.find((a: any) => Number(a.id) === id))
                .filter(Boolean) as any[];
        } else {
            this.selectedAppraiser = [];
        }

        const cust = params.customers;
        if (cust && String(cust).trim() !== '' && this.customersOptions && this.customersOptions.length > 0) {
            const ids = String(cust).split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
            this.selectedCustomer = ids
                .map(id => this.customersOptions.find((c: any) => Number(c.id) === id))
                .filter(Boolean) as any[];
        } else {
            this.selectedCustomer = [];
        }
    }

    visibilityModalFilterByDateRange(value) {
        this.visibleModalFilterByDateRange = value;
    }

    @Emit()
    searchAction():ClaimTableFilters | undefined{
        if(this.loadingProperties) {
            return
        } 

        let filters:ClaimTableFilters = {};

        if(this.searchQuery && this.searchQuery.trim() !== ''){
            filters['query'] = this.searchQuery.trim();
        }

        if(this.filtersEventTypeSelected !== null){
            filters['event_type'] = this.filtersEventTypeSelected['id'];
        }
        

        if(this.filtersDateRangeSelected && this.filtersDateRangeSelected.length > 0){
            filters['start_date'] = this.filtersDateRangeSelected[0];
            filters['end_date'] = this.filtersDateRangeSelected[1];
        }

        if(filters.start_date === ''){
            delete filters.start_date;
        }
        if(filters.end_date === ''){
            delete filters.end_date;
        }
        if(this.selectedAppraiser.length > 0){
            filters['appraisers'] = this.selectedAppraiser.map(a => a.id).join(',');
        }else{
            delete filters['appraisers'];
        }

        if(this.selectedCustomer.length > 0){
            filters['customers'] = this.selectedCustomer.map(c => c.id).join(',');
        }else{
            delete filters['customers'];
        }

        return filters;
    }

   
}
